"use client";

import { useCallback, useMemo, useState } from "react";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  INDIGENOUS_KNOWLEDGE_FIELDS as F,
  formatIndigenousKnowledge,
  type IndigenousKnowledgeLabels,
  indigenousKnowledgeIsReady,
  type KnowledgeSource,
} from "@/lib/tool-kit/special-sources/indigenous-knowledge-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const INDIGENOUS_KNOWLEDGE_CITATION_GENERATOR_STRINGS = {
  formHeading: "About the teaching",
  resultsHeading: "Your citations",
  emptyPromptUnrecorded:
    "Enter the person's name and their Nation or community to see your citations.",
  emptyPromptRecorded:
    "Enter the title of the recording to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Show placeholder example",
  clear: "Clear",
  sourceLabel: "How was the knowledge shared with you?",
  sourceUnrecorded: "In person, and not recorded",
  sourceRecorded: "In a recording you can link to",
  introUnrecorded:
    "Before you cite: ask the Elder or Knowledge Keeper for permission to include their name and teaching, ask how they would like to be named — including their Nation, any title and the spelling of their name — and follow their community's protocol. Afterwards, check with them that what you wrote is accurate and appropriate to share.",
  introRecorded:
    "A teaching that was recorded and published is cited like any recording, with a reference list entry. Check that it is appropriate to share in your work — some stories are told only at certain times of year or by certain people.",
  firstNameLabel: "First name(s)",
  firstNamePlaceholder: "Firstname",
  lastNameLabel: "Last name",
  lastNamePlaceholder: "Lastname",
  lastNameHint:
    "Leave this blank if the person uses one name, or a name that should not be reordered.",
  nationLabel: "Nation or community",
  nationPlaceholder: "Nation or community",
  nationHint:
    "As the person identifies themselves, e.g. a specific First Nation.",
  treatyLabel: "Treaty territory (optional)",
  treatyPlaceholder: "Treaty territory",
  treatyHint: "Only if it applies and the person wants it included.",
  residenceLabel: "Where they live (optional)",
  residencePlaceholder: "City or community",
  topicLabel: "Topic or description of the teaching (optional)",
  topicPlaceholder: "Oral teaching",
  topicHint:
    "Discuss with the Knowledge Keeper how they would describe what they shared.",
  dateLabel: "Date you spoke with them",
  datePlaceholder: "YYYY-MM-DD",
  dateHint:
    "An exact date if you have one; a month (YYYY-MM), a year, or a range such as March–May 2024 if you met over time.",
  speakerFirstLabel: "Speaker's first name(s) (optional)",
  speakerLastLabel: "Speaker's last name (optional)",
  uploaderLabel: "Who published the recording",
  uploaderPlaceholder: "Name of the organization",
  uploaderHint:
    "The account, archive or organization that made it available. APA names it as the author.",
  titleLabel: "Title of the recording",
  titlePlaceholder: "Title of the recorded teaching",
  mediumLabel: "Recording type",
  mediumVideo: "Video",
  mediumAudio: "Audio",
  siteLabel: "Website, platform or archive",
  sitePlaceholder: "Name of the archive or platform",
  publishedLabel: "Date published",
  urlLabel: "URL",
  urlPlaceholder: "https://",
  apaLabel: "APA 7th edition",
  templateApaLabel:
    "Elder & Knowledge Keeper template — APA (MacLeod / NorQuest)",
  templateMlaLabel:
    "Elder & Knowledge Keeper template — MLA (MacLeod / NorQuest)",
  mlaLabel: "MLA 9th edition",
  referenceList: "Reference list",
  worksCited: "Works Cited",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  inText: "In-text citation",
  namingSpeaker: "Naming the speaker in your sentence",
  apaNote:
    "APA 7 cites unrecorded knowledge in the text only, with no reference list entry, because readers cannot retrieve it. Use the person's full name, and describe what they shared in your sentence.",
  templateApaNote:
    "Lorisia MacLeod's template, as NorQuest College Library publishes it: the Elder or Knowledge Keeper also gets a reference list entry. It is not part of official APA Style — check that your instructor or publisher accepts it.",
  templateMlaNote:
    "The MLA version of the same template, with the date written day, month, year.",
  recordedApaNote:
    "APA names whoever published the recording as its author. Name the Elder or Knowledge Keeper, and their Nation, in your sentence.",
  recordedMlaNote:
    "MLA lists the speaker as the author and names the account that uploaded the recording after the platform.",
  exampleUnrecorded: {
    firstName: "Firstname",
    lastName: "Lastname",
    nation: "Nation or community",
    treaty: "Treaty territory",
    residence: "City or community",
    topic: "Oral teaching",
    date: "2024-04-04",
  },
  exampleRecorded: {
    firstName: "Firstname",
    lastName: "Lastname",
    nation: "Nation or community",
    uploader: "Name of the organization",
    title: "Title of the recorded teaching",
    medium: "video",
    site: "Name of the archive or platform",
    date: "2023-06-21",
    url: "https://www.example.org/recording",
  },
} as const;

type Strings = typeof INDIGENOUS_KNOWLEDGE_CITATION_GENERATOR_STRINGS;

function unrecordedFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.firstName,
      kind: "text",
      label: strings.firstNameLabel,
      placeholder: strings.firstNamePlaceholder,
    },
    {
      id: F.lastName,
      kind: "text",
      label: strings.lastNameLabel,
      placeholder: strings.lastNamePlaceholder,
      hint: strings.lastNameHint,
    },
    {
      id: F.nation,
      kind: "text",
      label: strings.nationLabel,
      placeholder: strings.nationPlaceholder,
      hint: strings.nationHint,
    },
    {
      id: F.treaty,
      kind: "text",
      label: strings.treatyLabel,
      placeholder: strings.treatyPlaceholder,
      hint: strings.treatyHint,
    },
    {
      id: F.residence,
      kind: "text",
      label: strings.residenceLabel,
      placeholder: strings.residencePlaceholder,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
      hint: strings.dateHint,
    },
    {
      id: F.topic,
      kind: "text",
      label: strings.topicLabel,
      placeholder: strings.topicPlaceholder,
      hint: strings.topicHint,
      wide: true,
    },
  ];
}

function recordedFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      wide: true,
    },
    {
      id: F.uploader,
      kind: "text",
      label: strings.uploaderLabel,
      placeholder: strings.uploaderPlaceholder,
      hint: strings.uploaderHint,
    },
    {
      id: F.medium,
      kind: "select",
      label: strings.mediumLabel,
      options: [
        { value: "video", label: strings.mediumVideo },
        { value: "audio", label: strings.mediumAudio },
      ],
    },
    {
      id: F.site,
      kind: "text",
      label: strings.siteLabel,
      placeholder: strings.sitePlaceholder,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.publishedLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      wide: true,
    },
    {
      id: F.firstName,
      kind: "text",
      label: strings.speakerFirstLabel,
      placeholder: strings.firstNamePlaceholder,
    },
    {
      id: F.lastName,
      kind: "text",
      label: strings.speakerLastLabel,
      placeholder: strings.lastNamePlaceholder,
    },
    {
      id: F.nation,
      kind: "text",
      label: strings.nationLabel,
      placeholder: strings.nationPlaceholder,
      hint: strings.nationHint,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function indigenousKnowledgeLabels(
  strings: Strings
): IndigenousKnowledgeLabels {
  return {
    apa: strings.apaLabel,
    templateApa: strings.templateApaLabel,
    templateMla: strings.templateMlaLabel,
    mla: strings.mlaLabel,
    referenceList: strings.referenceList,
    worksCited: strings.worksCited,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    inText: strings.inText,
    namingSpeaker: strings.namingSpeaker,
    apaNote: strings.apaNote,
    templateApaNote: strings.templateApaNote,
    templateMlaNote: strings.templateMlaNote,
    recordedApaNote: strings.recordedApaNote,
    recordedMlaNote: strings.recordedMlaNote,
  };
}

export default function IndigenousKnowledgeCitationGeneratorWidget() {
  const strings = useToolUi(INDIGENOUS_KNOWLEDGE_CITATION_GENERATOR_STRINGS);
  const [source, setSource] = useState<KnowledgeSource>("unrecorded");
  const recorded = source === "recorded";
  const fields = useMemo(
    () => (recorded ? recordedFields(strings) : unrecordedFields(strings)),
    [recorded, strings]
  );
  const labels = useMemo(() => indigenousKnowledgeLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatIndigenousKnowledge(values, source, labels),
    [source, labels]
  );
  const isReady = useCallback(
    (values: SourceValues) => indigenousKnowledgeIsReady(values, source),
    [source]
  );
  const builderStrings = useMemo(
    () => ({
      ...strings,
      emptyPrompt: recorded
        ? strings.emptyPromptRecorded
        : strings.emptyPromptUnrecorded,
    }),
    [recorded, strings]
  );

  return (
    <SourceCitationBuilder
      example={recorded ? strings.exampleRecorded : strings.exampleUnrecorded}
      fields={fields}
      format={format}
      intro={
        <div className="space-y-3">
          <PillGroup
            label={strings.sourceLabel}
            name="indigenous-knowledge-source"
            onChange={setSource}
            options={[
              { value: "unrecorded", label: strings.sourceUnrecorded },
              { value: "recorded", label: strings.sourceRecorded },
            ]}
            value={source}
          />
          <Notice>
            {recorded ? strings.introRecorded : strings.introUnrecorded}
          </Notice>
        </div>
      }
      isReady={isReady}
      strings={builderStrings}
    />
  );
}
